<template>
  <v-container>
    <SimplePageHeader :title="pageTitle" class="px-4 pr-5 mb-5" />
    <v-stepper v-model="step" :non-linear="isEditing" class="elevation-2 mb-4">
      <v-stepper-header>
        <v-stepper-step step="1" :complete="step > 1" :editable="true">Template Info</v-stepper-step>
        <v-divider />
        <v-stepper-step step="2" :editable="isEditing || step > 1">Tasks</v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <!-- Step 1: Template Details -->
        <v-stepper-content step="1">
          <v-form ref="detailsForm" class="pa-6">
            <v-row>
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="name"
                  label="Template Name *"
                  outlined
                  :rules="[v => !!v || 'Name is required']"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-switch v-model="isActive" label="Active" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="typeId"
                  :items="workflowTypeList"
                  item-text="name"
                  item-value="id"
                  label="Workflow Type *"
                  outlined
                  :rules="[v => !!v || 'Type is required']"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <ClientsDropDownList
                  :id="clientId"
                  :clearable="true"
                  @clientIdChange="clientId = $event"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6">
                <UsersDropDownList
                  :userId="defaultAdminUserId"
                  :isRequired="true"
                  :onlyActiveUsers="true"
                  :securityRoles="[SUPER_ADMIN, ADMIN]"
                  label="Default Admin *"
                  @userIdChange="defaultAdminUserId = $event"
                  @userNameChange="onAdminUserNameChange"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="dealAssignmentTypeMatch"
                  :items="dealAssignmentTypeItems"
                  label="Deal Type"
                  hint="Restrict this template to a deal type. Leave blank to match any."
                  persistent-hint
                  clearable
                  outlined
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="contractTypeMatch"
                  :items="contractTypeItems"
                  label="Contract Type"
                  :disabled="!isContractorMatch"
                  :hint="isContractorMatch ? 'Restrict to a contract type. Leave blank to match any.' : 'Only applies when Deal Type is Contractor.'"
                  persistent-hint
                  clearable
                  outlined
                />
              </v-col>
            </v-row>
          </v-form>
        </v-stepper-content>

        <!-- Step 2: Tasks -->
        <v-stepper-content step="2">
          <div class="pa-4">
            <div class="d-flex justify-end mb-3">
              <v-btn color="primary" outlined small @click="openAddTask">
                <v-icon small left>add</v-icon>Add Task
              </v-btn>
            </div>
            <v-data-table
              dense
              :headers="taskHeaders"
              :items="workflowTemplateTaskList"
              :loading="tasksLoading"
            >
              <template v-slot:item.taskTypeName="{ item }">
                {{ item.taskTypeName || 'Manual' }}
              </template>
              <template v-slot:item.defaultAssigneeRoleId="{ item }">
                {{ item.defaultAssigneeRoleId || '—' }}
              </template>
              <template v-slot:item.dueDateInfo="{ item }">
                {{ item.dueDateAnchorName }} {{ item.dueDateOffsetDays >= 0 ? '+' : '' }}{{ item.dueDateOffsetDays }}d
              </template>
              <template v-slot:item.blockerTaskTitle="{ item }">
                {{ item.blockerTaskTitle || '—' }}
              </template>
              <template v-slot:item.action="{ item }">
                <v-btn outlined color="primary" class="mx-1" @click="openEditTask(item)">
                  <v-icon>edit</v-icon>
                </v-btn>
                <v-btn outlined color="red" class="mx-1" @click="confirmDeleteTask(item)">
                  <v-icon>delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </div>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>

    <!-- Navigation Buttons -->
     <v-row justify="space-between" class="mt-1">
      <v-col
        cols="6"
        >
        <v-btn
          width="10em"
          color="secondary"
          class="mr-4"
          @click="saveAndExit"
          >
          Save & Exit
        </v-btn>
      </v-col>
      <v-col
        cols="6"
        class="text-right"
      >
        <v-btn
          color="secondary"
          class="mr-4"
          width="10em"
          outlined
          :disabled="isBackButtonDisabled"
          @click="goBack"
        >
          Back
        </v-btn>
        <v-btn
          color="primary"
          width="10em"
          :disabled="isNextButtonDisabled"
          @click="goNext"
        >
          Next
        </v-btn>
      </v-col>
    </v-row>

    <ConfirmationModal
      title="Delete Task"
      :contentText="taskToDelete ? `Delete task &quot;${taskToDelete.title}&quot;?` : ''"
      :showModal="showDeleteConfirmation"
      toolbarColor="error"
      okText="Delete"
      @ok-click="handleDeleteConfirmed"
      @cancel-click="showDeleteConfirmation = false; taskToDelete = null"
    />

    <!-- Task Edit Dialog -->
    <WorkflowTemplateTaskEdit
      v-if="showTaskDialog"
      :task="editingTask"
      :templateId="templateIdAsInt"
      :templateClientId="clientId"
      :existingTasks="workflowTemplateTaskList"
      @save="handleSaveTask"
      @close="showTaskDialog = false"
    />

    <BottomSpacer />
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { workflowTemplatesRoute, workflowTemplateEditRoute } from '@/lib/constants/routes'
import { SimplePageHeader } from '@/components/pageComponents'
import { BottomSpacer } from '@/components/buttons'
import { ConfirmationModal } from '@/components/dialogs'
import { ClientsDropDownList } from '@/components/clients'
import { UsersDropDownList } from '@/components/users'
import { SUPER_ADMIN, ADMIN } from '@/lib/constants/securityRoles'
import { DEAL_ASSIGNMENT_TYPES, CONTRACTOR } from '@/lib/constants/dealAssignmentTypes'
import { CONTRACT_TYPES } from '@/lib/constants/contractTypes'
import WorkflowTemplateTaskEdit from './WorkflowTemplateTaskEdit'
import { notifyError } from '@/lib/notifications'

export default {
  components: {
    SimplePageHeader,
    BottomSpacer,
    ConfirmationModal,
    ClientsDropDownList,
    UsersDropDownList,
    WorkflowTemplateTaskEdit
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.previousRoute = from
    })
  },
  data () {
    return {
      previousRoute: null,
      workflowTemplatesRoute,
      SUPER_ADMIN,
      ADMIN,
      step: 1,
      showTaskDialog: false,
      editingTask: {},
      tasksLoading: false,
      showDeleteConfirmation: false,
      taskToDelete: null,
      taskHeaders: [
        { text: '#', value: 'sortOrder', width: '5%' },
        { text: 'Title', value: 'title' },
        { text: 'Type', value: 'taskTypeName', width: '13%' },
        { text: 'Assignee Role', value: 'defaultAssigneeRoleId', width: '13%' },
        { text: 'Due Date', value: 'dueDateInfo', width: '16%' },
        { text: 'Blocked By', value: 'blockerTaskTitle', width: '13%' },
        { text: 'Actions', value: 'action', align: 'center', sortable: false, width: '20%' }
      ]
    }
  },
  computed: {
    ...mapFields('workflowTemplate', ['id', 'name', 'typeId', 'clientId', 'isActive', 'defaultAdminUserId', 'defaultAdminUserName', 'dealAssignmentTypeMatch', 'contractTypeMatch']),
    ...mapFields('workflowTemplateTasks', ['workflowTemplateTaskList']),
    ...mapFields('workflowTypes', ['workflowTypeList']),
    isBackButtonDisabled () {
      return this.step <= 1
    },
    isNextButtonDisabled () {
      return this.step >= 2
    },
    dealAssignmentTypeItems () {
      return Object.entries(DEAL_ASSIGNMENT_TYPES).map(([value, text]) => ({ value: Number(value), text }))
    },
    contractTypeItems () {
      return Object.entries(CONTRACT_TYPES).map(([value, text]) => ({ value: Number(value), text }))
    },
    isContractorMatch () {
      return this.dealAssignmentTypeMatch === CONTRACTOR
    },
    isEditing () {
      return !!this.$route.params.id
    },
    templateIdAsInt () {
      return parseInt(this.$route.params.id) || this.id
    },
    pageTitle () {
      return this.isEditing ? 'Edit Workflow Template' : 'New Workflow Template'
    }
  },
  watch: {
    dealAssignmentTypeMatch (value) {
      // Contract type only applies to Contractor deals; clear it otherwise.
      if (value !== CONTRACTOR && this.contractTypeMatch !== null) {
        this.contractTypeMatch = null
      }
    }
  },
  methods: {
    ...mapActions('workflowTemplate', ['getWorkflowTemplate', 'saveWorkflowTemplate', 'clearWorkflowTemplate']),
    ...mapActions('workflowTemplateTasks', ['getWorkflowTemplateTasks', 'deleteWorkflowTemplateTask', 'saveWorkflowTemplateTask']),
    ...mapActions('workflowTypes', ['getWorkflowTypes']),
    onAdminUserNameChange (firstName, lastName) {
      this.defaultAdminUserName = `${firstName} ${lastName}`
    },
    async goBack () {
      if (this.step > 1) {
        const saved = await this.handleSave()
        if (saved) this.step--
      }
    },
    async goNext () {
      if (this.step < 2) {
        const saved = await this.handleSave()
        if (saved) this.step++
      }
    },
    handleSave (navigateToEditor = true) {
      if (!this.$refs.detailsForm.validate()) return Promise.resolve(false)
      const isEditing = this.isEditing
      return this.saveWorkflowTemplate()
        .then(response => {
          const templateId = response.data.id || this.templateIdAsInt
          if (!isEditing && navigateToEditor) {
            this.$router.replace({ name: workflowTemplateEditRoute.name, params: { id: templateId } })
          }

          if (!navigateToEditor && !isEditing) return true

          // Store clears state after save; wait for the reload before changing steps.
          return this.getWorkflowTemplate({ id: templateId })
            .then(template => {
              if (!template) return false
              this.getWorkflowTemplateTasks(templateId)
              return true
            })
        })
        .catch(() => false)
    },
    async saveAndExit () {
      const saved = await this.handleSave(false)
      if (!saved) return

      if (this.previousRoute && this.previousRoute.name === workflowTemplatesRoute.name) {
        this.$router.back()
      } else {
        // Fallback to Vue Router navigation which resets the filters, sorting, and pagination
        this.$router.push({ name: workflowTemplatesRoute.name })
      }
    },
    openAddTask () {
      this.editingTask = { workflowTemplateId: this.templateIdAsInt }
      this.showTaskDialog = true
    },
    openEditTask (task) {
      this.editingTask = { ...task }
      this.showTaskDialog = true
    },
    handleSaveTask (taskData) {
      const action = taskData.id
        ? this.$store.dispatch('workflowTemplateTask/update', taskData)
        : this.$store.dispatch('workflowTemplateTask/save', taskData)

      action
        .then(response => {
          if (!response.data) {
            notifyError(this.$store.dispatch, 'Unable to save task')
            return
          }
          this.showTaskDialog = false
          this.getWorkflowTemplateTasks(this.templateIdAsInt)
        })
        .catch(() => notifyError(this.$store.dispatch, 'Unable to save task'))
    },
    confirmDeleteTask (task) {
      this.taskToDelete = task
      this.showDeleteConfirmation = true
    },
    handleDeleteConfirmed () {
      this.showDeleteConfirmation = false
      this.deleteWorkflowTemplateTask({ templateId: this.templateIdAsInt, taskId: this.taskToDelete.id })
      this.taskToDelete = null
    }
  },
  created () {
    this.getWorkflowTypes()
    if (this.isEditing) {
      this.getWorkflowTemplate({ id: this.$route.params.id })
        .then(() => {
          this.getWorkflowTemplateTasks(this.templateIdAsInt)
        })
    } else {
      this.clearWorkflowTemplate()
    }
  }
}
</script>
